import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  Typography,
} from "@mui/material";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  BalanceInformationSection,
  GeneralInformationSection,
} from "@/features/outlet-chart-of-accounts/outlet-chart-of-account";
import { PermissionEnum } from "@/features/permissions";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, CustomDialogTitle, useSnackbar } from "@/features/ui";

export const OutletChartOfAccount = () => {
  const { id } = useParams();
  const { outlet, hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [chartOfAccount, setChartOfAccount] = useState(null);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadChartOfAccount = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`chart-of-accounts/${id}`, {
        params: {
          withParent: true,
          withTotalBalance: true,
          withInitialBalanceTransaction: true,
        },
        headers: {
          "Outlet-Id": outlet.id,
        },
      });
      setChartOfAccount(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat akun outlet")
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, id]);

  useEffect(() => {
    loadChartOfAccount();
  }, [loadChartOfAccount]);

  const handleDelete = async () => {
    if (!chartOfAccount || !outlet) return;

    try {
      setIsDeleting(true);
      await apiOutletClient.delete(`chart-of-accounts/${chartOfAccount.id}`, {
        headers: {
          "Outlet-Id": outlet.id,
        },
      });
      showSnackbar("Akun berhasil dihapus", "success");
      navigate("/general-ledger/outlet-chart-of-accounts");
    } catch (error) {
      // Show error message from backend or fallback message
      showSnackbar(getErrorMessage(error, "Gagal menghapus akun"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Check if a node is a leaf (no children) in nested set model
  const isLeafNode = (account) => {
    return account.rgt - account.lft === 1;
  };

  const canEdit =
    hasPermission(PermissionEnum.UPDATE_OUTLET_CHART_OF_ACCOUNTS) &&
    chartOfAccount?.isOutletSpecific;

  const canDelete =
    hasPermission(PermissionEnum.DELETE_OUTLET_CHART_OF_ACCOUNTS) &&
    chartOfAccount?.isOutletSpecific &&
    chartOfAccount &&
    isLeafNode(chartOfAccount);

  const toolbarContent = (
    <>
      {canDelete && (
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Hapus
        </Button>
      )}
      {canEdit && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate("edit")}
        >
          Ubah
        </Button>
      )}
    </>
  );

  if (loading) {
    return <CenteredLoading sx={{ height: "80vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Akun Perkiraan Outlet"
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={3}>
        <GeneralInformationSection chartOfAccount={chartOfAccount} />
        <BalanceInformationSection chartOfAccount={chartOfAccount} />

        {/* Delete Confirmation Dialog */}
        {deleteDialogOpen && (
          <Dialog
            open
            onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <CustomDialogTitle
              title="Hapus Akun"
              onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            />
            <DialogContent>
              <DialogContentText>
                Apakah Anda yakin ingin menghapus akun{" "}
                <strong>{chartOfAccount.name}</strong> ({chartOfAccount.code})?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                Batal
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={isDeleting}
              >
                {isDeleting ? "Menghapus..." : "Hapus"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Stack>
    </PageContainer>
  );
};
